/// <reference path="Utils.ts" />
namespace RS.Localisation {
    /**
    * Settings for a currency.
    */
    export interface CurrencySettings
    {
        /** Name of the currency. */
        name: string;

        /** The currency symbol. */
        symbol: string;

        /** Exchange rate. */
        exchangeRate?: number;

        /** Number of digits to group together. */
        grouping: number;

        /** Separator symbol used between groups of digits. */
        groupingSeparator: string;

        /** Number of digits used to represent fractional currency. */
        fractionalDigits: number;

        /** Seperator symbol used betweeen whole and fractional currency. */
        fractionalSeparator: string;

        /** Space between currency code and numerical amount. */
        currencyCodeSpacing: number;

        /** Should the currency code should be before amount? */
        currencyCodeBeforeAmount: boolean;

        /** Space between currency symbol and numerical amount. */
        currencySymbolSpacing: number;

        /** Should the currency symbol should be before the amount? */
        currencySymbolBeforeAmount: boolean;

        /** The multiplier for currency values. */
        currencyMultiplier: number;

        /** Should a currency symbol be added? */
        displaySymbol: boolean;

        /** Should a currency code be added? */
        displayCode: boolean;
    }

    const standardCurrencySettings =
    {
        exchangeRate: 1,
        grouping: 3,
        groupingSeparator: ",",
        fractionalDigits: 2,
        fractionalSeparator: ".",
        currencyCodeSpacing: 1,
        currencyCodeBeforeAmount: true,
        currencySymbolSpacing: 0,
        currencySymbolBeforeAmount: true,
        currencyMultiplier: 1,
        displaySymbol: true,
        displayCode: false,
    };

    const spaceCharacter = RS.Localisation.SpecialChars.NonBreakingSpace;

    export const currencyMap: { [code: string]: CurrencySettings } =
    {
        "GBP":
        {
            ...standardCurrencySettings,
            name: "GBP",
            symbol: "£",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 1,
        },
        "HKD":
        {
            ...standardCurrencySettings,
            name: "HKD",
            symbol: "$",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 10,
        },
        "EUR":
        {
            ...standardCurrencySettings,
            name: "EUR",
            symbol: "€",
            groupingSeparator: ".",
            fractionalSeparator: ",",
            currencyMultiplier: 1,
        },
        "USD":
        {
            ...standardCurrencySettings,
            name: "USD",
            symbol: "$",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 1,
        },
        "JPY":
        {
            ...standardCurrencySettings,
            name: "JPY",
            symbol: "¥",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            fractionalDigits: 0,
            currencySymbolBeforeAmount: false,
            currencyMultiplier: 100
        },
        "SGD":
        {
            ...standardCurrencySettings,
            name: "SGD",
            symbol: "$",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 1,
        },
        "AUD":
        {
            ...standardCurrencySettings,
            name: "AUD",
            symbol: "$",
            groupingSeparator: " ",
            fractionalSeparator: ".",
            currencyMultiplier: 1,
        },
        "CAD":
        {
            ...standardCurrencySettings,
            name: "CAD",
            symbol: "$",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 1,
        },
        "CHF":
        {
            ...standardCurrencySettings,
            name: "CHF",
            symbol: "SFr.",
            groupingSeparator: "'",
            fractionalSeparator: ".",
            currencySymbolSpacing: 0,
            currencyMultiplier: 1,
        },
        "DKK":
        {
            ...standardCurrencySettings,
            name: "DKK",
            symbol: "kr",
            groupingSeparator: ".",
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencyMultiplier: 5,
        },
        "SEK":
        {
            ...standardCurrencySettings,
            name: "SEK",
            symbol: "kr",
            groupingSeparator: ".",
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencyMultiplier: 10,
        },
        "ARS":
        {
            ...standardCurrencySettings,
            name: "ARS",
            symbol: "$",
            groupingSeparator: ".",
            fractionalSeparator: ",",
            currencyMultiplier: 500,
        },
        "BGN":
        {
            ...standardCurrencySettings,
            name: "BGN",
            symbol: "лв.",
            groupingSeparator: spaceCharacter,
            fractionalSeparator: ",",
            currencySymbolBeforeAmount: false,
            currencyMultiplier: 1,
        },
        "BRL":
        {
            ...standardCurrencySettings,
            name: "BRL",
            symbol: "R$",
            groupingSeparator: ".",
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencyMultiplier: 1,
        },
        "CNY":
        {
            ...standardCurrencySettings,
            name: "CNY",
            symbol: "¥",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 5,
        },
        "CZK":
        {
            ...standardCurrencySettings,
            name: "CZK",
            symbol: "Kč",
            groupingSeparator: spaceCharacter, 
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencySymbolBeforeAmount: false,
            currencyMultiplier: 20,
        },
        "EEK":
        {
            ...standardCurrencySettings,
            name: "EEK",
            symbol: "€",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            currencyMultiplier: 15,
        },
        "HRK":
        {
            ...standardCurrencySettings,
            name: "HRK",
            symbol: "Kn",
            groupingSeparator: ".",
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencyMultiplier: 5,
        },
        "HUF": 
        {
            ...standardCurrencySettings,
            name: "HUF",
            symbol: "Ft",
            groupingSeparator: spaceCharacter,
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencySymbolBeforeAmount: false,
            currencyMultiplier: 300,
        },
        "INR":
        {
            ...standardCurrencySettings,
            name: "INR",
            symbol: "₹",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 40,
        },
        "ISK":
        {
            ...standardCurrencySettings,
            name: "ISK",
            symbol: "kr.",
            groupingSeparator: ".",
            fractionalDigits: 0,
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencySymbolBeforeAmount: false,
            currencyMultiplier: 100,
        },
        "LIL":
        {
            ...standardCurrencySettings,
            name: "LTL",
            symbol: "€",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            currencyMultiplier: 4,
        },
        "LVL":
        {
            ...standardCurrencySettings,
            name: "LVL",
            symbol: "€",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            currencyMultiplier: 1,
        },
        "MXN": 
        { 
            ...standardCurrencySettings,
            name: "MXN",
            symbol: "$",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 20,
        },
        "MYR":
        {
            ...standardCurrencySettings,
            name: "MYR",
            symbol: "RM",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 5,
        },
        "NOK": 
        {
            ...standardCurrencySettings,
            name: "NOK",
            symbol: "kr",
            groupingSeparator: spaceCharacter,
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencyMultiplier: 10,
        },
        "NZD": 
        {
            ...standardCurrencySettings,
            name: "NZD",
            symbol: "$", 
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 1,
        },
        "PEN":
        {
            ...standardCurrencySettings,
            name: "PEN",
            symbol: "S/.",
            groupingSeparator: ".",
            fractionalSeparator: ",",
            currencyMultiplier: 1,
        },
        "PLN":
        {
            ...standardCurrencySettings,
            name: "PLN",
            symbol: "zł",
            groupingSeparator: spaceCharacter,
            fractionalSeparator: ",",
            currencySymbolBeforeAmount: false,
            currencyMultiplier: 4,
        },
        "RON":
        {
            ...standardCurrencySettings,
            name: "RON",
            symbol: "LEI",
            groupingSeparator: ".",
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencySymbolBeforeAmount: false,
            currencyMultiplier: 5,
        },
        "RUB":
        {
            ...standardCurrencySettings,
            name: "RUB",
            symbol: "руб.",
            groupingSeparator: spaceCharacter,
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencySymbolBeforeAmount: false,
            currencyMultiplier: 40,
        },
        "THB":
        {
            ...standardCurrencySettings,
            name: "THB",
            symbol: "฿",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 40,
        },
        "TRY":
        {
            ...standardCurrencySettings,
            name: "TRY",
            symbol: "TL",
            groupingSeparator: ".",
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencySymbolBeforeAmount: false,
            currencyMultiplier: 5,
        },
        "TWD":
        {
            ...standardCurrencySettings,
            name: "TWD",
            symbol: "NT$",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencyMultiplier: 40,
        },
        "ZAR":
        {
            ...standardCurrencySettings,
            name: "ZAR",
            symbol: "R",
            groupingSeparator: ",",
            fractionalDigits: 2,
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            currencyMultiplier: 3,
        },
        "PHP":
        {
            ...standardCurrencySettings,
            name: "PHP",
            symbol: "₱",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            currencyMultiplier: 40,
        },
        "PYG":
        {
            ...standardCurrencySettings,
            name: "PYG",
            symbol: "₲",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            currencyMultiplier: 3750
        },
        "AMD":
        {
            ...standardCurrencySettings,
            name: "AMD",
            symbol: "֏",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            currencyMultiplier: 300
        },
        "BIF":
        {
            ...standardCurrencySettings,
            name: "BIF",
            symbol: "FBu",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 2000,
        },
        "CDF":
        {
            ...standardCurrencySettings,
            name: "CDF",
            symbol: "FC",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 2000,
        },
        "COP":
        {
            ...standardCurrencySettings,
            name: "COP",
            symbol: "$",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 2000,
        },
        "GEL":
        {
            ...standardCurrencySettings,
            name: "GEL",
            symbol: "₾",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 1,
        },
        "IDR":
        {
            ...standardCurrencySettings,
            name: "IDR",
            symbol: "Rp",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 20000,
        },
        "ILS":
        {
            ...standardCurrencySettings,
            name: "ILS",
            symbol: "₪",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 1000,
        },
        "KHR":
        {
            ...standardCurrencySettings,
            name: "KHR",
            symbol: "៛",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 1000,
        },
        "KRW":
        {
            ...standardCurrencySettings,
            name: "KRW",
            symbol: "₩",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 1000,
        },
        "MMK":
        {
            ...standardCurrencySettings,
            name: "MMK",
            symbol: "K",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 1000,
        },
        "NGN":
        {
            ...standardCurrencySettings,
            name: "NGN",
            symbol: "₦",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 300,
        },
        "RSD":
        {
            ...standardCurrencySettings,
            name: "RSD",
            symbol: "дин.",
            groupingSeparator: spaceCharacter,
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencySymbolBeforeAmount: false,
            fractionalDigits: 0,
            currencyMultiplier: 100,
        },
        "UAH":
        {
            ...standardCurrencySettings,
            name: "UAH",
            symbol: "₴",
            groupingSeparator: spaceCharacter,
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            currencySymbolBeforeAmount: false,
            fractionalDigits: 0,
            currencyMultiplier: 20,
        },
        "UYU":
        {
            ...standardCurrencySettings,
            name: "UYU",
            symbol: "$",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 20,
        },
        "VEF":
        {
            ...standardCurrencySettings,
            name: "VEF",
            symbol: "Bs",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 10,
        },
        "VND":
        {
            ...standardCurrencySettings,
            name: "VND",
            symbol: "₫",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 20000,
        },
        "XAF":
        {
            ...standardCurrencySettings,
            name: "XAF",
            symbol: "FCFA",
            groupingSeparator: spaceCharacter,
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 300,
        },
        "XOF":
        {
            ...standardCurrencySettings,
            name: "XOF",
            symbol: "CFA",
            groupingSeparator: spaceCharacter,
            fractionalSeparator: ",",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 1000,
        },
        "ZMW":
        {
            ...standardCurrencySettings,
            name: "ZMW",
            symbol: "ZK",
            groupingSeparator: ",",
            fractionalSeparator: ".",
            currencySymbolSpacing: 1,
            fractionalDigits: 0,
            currencyMultiplier: 10,
        }
    };

    export const defaultCurrencySettings = currencyMap["GBP"];
}
